import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
// import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import { fetchEmployees } from "../../../store/slices/employeeAdminSlice";
import {
  fetchWorkEntries,
  fetchWorkEntriesProjectTracking,
} from "../../../store/slices/workentriesSlice";
import { fetchAllProjects } from "../../../store/slices/projectsSlice";
import {
  fetchHardwareDescriptions,
  fetchHardwareProjectTracking,
} from "../../../store/slices/hardwareDescriptionSlice";
import {
  fetchDescriptionTasks,
  fetchDescriptionTasksProjectTracking,
} from "../../../store/slices/DescriptionTask";
import { fetchQuotations } from "../../../store/slices/hourlyRates";
import { fetchOtherExpenses } from "../../../store/slices/otherexpenses";
import { fetchSalesTaxes } from "../../../store/slices/salesTaxesSlice";
import { fetchAllFinancial } from "../../../store/slices/editFinanceSlice";
import { setOtheeExpensesData } from "../../../store/slices/filteredDataSlice";
import { fetchCompanyProfiles } from "../../../store/slices/companyProfileSlice";
import { fetchLeads, updateLead } from "../../../store/slices/leadsSlice";
import { fetchGroups } from "../../../store/slices/groupSlice";
import FinanceData from "./FinanceData";
import FinanceFileUpload from "./FinanceFileUpload";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useCurrencySymbol } from "../../../utils/currencyUtils";
import CommaConvert from "../../../utils/CommaConvert";
import { fetchSalesHourlyRates } from "../../../store/slices/salesHourlyRateSlice";
import {
  getPerdiemAndAverageCostFromExpenses,
  resolveEntityId,
  scopeOtherExpensesForUser,
} from "../../../utils/salespersonRatesScope";
import { resolveInstallationProjectRowForTrainingRollup } from "../../../utils/trainingOtherExpenseSync";
import { resolveProjectRatesSource } from "../../Operations/Projectmanager/ManagerFinance/ManagerActualAllServiceTable";

const getFinanceDisplayLocale = () => {
  const countryCode = String(localStorage.getItem("countrycode") || "").trim();
  const region = String(localStorage.getItem("region") || "").trim().toUpperCase();
  const isUsLocale =
    countryCode === "+1" || countryCode === "1" || region === "US" || region === "USA";
  const isInLocale =
    countryCode === "+91" || countryCode === "91" || region === "IN" || region === "INDIA";
  return isInLocale ? "en-IN" : isUsLocale ? "en-US" : "en-US";
};

/** L2/L5: show other expenses from DB with decimals (no rounding). */
const createOtherExpenseCommaFormatter = () => {
  const locale = getFinanceDisplayLocale();
  const options = { maximumFractionDigits: 10, minimumFractionDigits: 0 };

  return (num) => {
    if (num === undefined || num === null) return "";
    const value = Number(num);
    if (isNaN(value)) return "";
    return value.toLocaleString(locale, options);
  };
};

/** L2/L5: show grand total with decimals (no rounding). */
const createTotalExpenseCommaFormatter = () => {
  const locale = getFinanceDisplayLocale();
  const options = { maximumFractionDigits: 10, minimumFractionDigits: 0 };

  return (num) => {
    if (num === undefined || num === null) return "";
    const value = Number(num);
    if (isNaN(value)) return "";
    return value.toLocaleString(locale, options);
  };
};

const isInstallationTypeService = (typeService) =>
  String(typeService || "")
    .toLowerCase()
    .includes("installation");

const getRowKey = (row) =>
  row?.quotationId != null && String(row.quotationId).trim() !== ""
    ? String(row.quotationId).trim()
    : row?.ProjectName ?? "";

/** Current installation manager from project assignment (post project-approved reassignments). */
const resolveCurrentInstallationManagerId = (sourceProject, employeesList = []) => {
  const managerEmail = Array.isArray(sourceProject?.AssignedManagerEmail)
    ? sourceProject.AssignedManagerEmail[0]
    : sourceProject?.AssignedManagerEmail;

  if (!managerEmail) return null;

  const managerEmp = (employeesList || []).find(
    (emp) =>
      emp.EmpEmail &&
      emp.EmpEmail.toLowerCase() === String(managerEmail).trim().toLowerCase()
  );

  return resolveEntityId(managerEmp?._id) || null;
};

/** Resolve Installation manager/employee person ids from hourly-rate quotations (Settings). */
const resolveInstallationPersonIdsFromQuotations = (
  quotationsList,
  salesPersonId,
  installationManagerId = null
) => {
  const ratesOwnerId = installationManagerId || salesPersonId;
  const scoped = ratesOwnerId
    ? (quotationsList || []).filter(
        (q) => String(q?.createdBy || "") === String(ratesOwnerId)
      )
    : quotationsList || [];

  let managerId = null;
  let employeeId = null;

  scoped.forEach((q) => {
    const designation = String(q?.designation || "").toLowerCase();
    const personId = q?.personid;
    if (!designation.includes("installation") || !personId) return;

    const isManagerDesignation =
      (designation.includes("manager") ||
        designation.includes("project manager") ||
        designation.includes(" pm")) &&
      !designation.includes("employee");
    const isEmployeeDesignation =
      (designation.includes("operations") || designation.includes("employee")) &&
      !designation.includes("manager");

    if (isManagerDesignation && !managerId) managerId = personId;
    if (isEmployeeDesignation && !employeeId) employeeId = personId;
  });

  return { managerId, employeeId };
};

/** SalesData labels Operations before Manager — keep manager-only roles for installation managers. */
const normalizeInstallationDisplayRoles = (roles, taskownerid, managerId, employeeId) => {
  const roleList = Array.isArray(roles) ? roles : [];
  const ownerId = String(taskownerid || "");
  const isManagerPerson =
    managerId && ownerId === String(managerId);
  const isEmployeePerson =
    employeeId && ownerId === String(employeeId);

  if (isManagerPerson) {
    const managerRoles = roleList.filter((role) => {
      const rl = String(role || "").toLowerCase();
      return rl.includes("manager") || rl.includes("project manager");
    });
    return managerRoles.length > 0 ? managerRoles : ["Project manager"];
  }

  if (isEmployeePerson) {
    const employeeRoles = roleList.filter((role) => {
      const rl = String(role || "").toLowerCase();
      return (
        !rl.includes("project manager") &&
        !(rl.includes("manager") && !rl.includes("operations"))
      );
    });
    return employeeRoles.length > 0 ? employeeRoles : ["Operations"];
  }

  return roleList;
};

const isWonLeadRow = (leadRow) => {
  if (!leadRow) return false;
  const status = String(leadRow?.Status ?? leadRow?.status ?? "")
    .trim()
    .toLowerCase();
  if (status === "won" || status === "won recurring") return true;
  if (leadRow?.isProjectQuoted === true || leadRow?.isProjectQuoted === 1) {
    return true;
  }
  return String(leadRow?.isProjectQuoted ?? "").toLowerCase() === "true";
};

const hasFrozenInstallRates = (ratesSource) =>
  ratesSource &&
  (ratesSource.perdiem != null ||
    ratesSource.avgcost != null ||
    ratesSource.averagecost != null);

const findInstallationSnapshotProject = (
  projectId,
  leadRow,
  projectsList,
  financeDataProjectList
) => {
  const all = [...(financeDataProjectList || []), ...(projectsList || [])];
  const unique = Array.from(
    new Map(all.map((p) => [String(p?._id), p])).values()
  );
  const isInstall = (p) => isInstallationTypeService(p?.TypeService);
  const qid =
    leadRow?.quotationId != null ? String(leadRow.quotationId).trim() : "";
  const pname = String(leadRow?.ProjectName || "").trim();

  if (projectId) {
    const byId = unique.find(
      (p) => String(p?._id) === String(projectId) && isInstall(p)
    );
    if (byId) return resolveProjectRatesSource(byId, projectsList);
  }

  const quotedInstallRows = unique.filter((p) => {
    if (!isInstall(p) || p.isProjecttracking === true) return false;
    if (qid && String(p.quotationId ?? "").trim() === qid) return true;
    if (pname && String(p.ProjectName || "").trim() === pname) return true;
    return false;
  });

  for (const row of quotedInstallRows) {
    const src = resolveProjectRatesSource(row, projectsList);
    if (hasFrozenInstallRates(src)) return src;
  }

  return quotedInstallRows[0]
    ? resolveProjectRatesSource(quotedInstallRows[0], projectsList)
    : null;
};

/** When project has no saved perdiem/avgcost, derive frozen trip rates from stored task rows. */
const inferFrozenTripRatesFromTasks = (tasks, projectId, personId) => {
  const descriptions = (tasks || []).filter(
    (desc) =>
      String(desc?.projectid || "") === String(projectId) &&
      String(desc?.taskownerid || "") === String(personId) &&
      isInstallationTypeService(desc?.typeservice)
  );
  if (!descriptions.length) return null;

  let maxTrip = 0;
  let valueSum = 0;
  descriptions.forEach((desc) => {
    valueSum += Number(desc.value) || 0;
    if (desc.tripduration) {
      maxTrip = Math.max(maxTrip, Number(desc.tripduration) || 0);
    }
  });

  const storedExpenseSum = descriptions.reduce(
    (sum, desc) => sum + (Number(desc.expenses ?? desc.value) || 0),
    0
  );

  if (maxTrip > 0 && storedExpenseSum > valueSum) {
    const tripCombined = (storedExpenseSum - valueSum) / maxTrip;
    return {
      perdiem: tripCombined,
      averagecost: 0,
      useStoredTaskExpensesOnly: false,
    };
  }

  if (storedExpenseSum > 0) {
    return { perdiem: 0, averagecost: 0, useStoredTaskExpensesOnly: true };
  }

  return null;
};

const sumInstallationOtherExpensesForPerson = (
  tasks,
  projectId,
  personId,
  perdiem,
  averagecost,
  useStoredTaskExpensesOnly = false
) => {
  const descriptions = (tasks || []).filter(
    (desc) =>
      String(desc?.projectid || "") === String(projectId) &&
      String(desc?.taskownerid || "") === String(personId) &&
      isInstallationTypeService(desc?.typeservice)
  );

  let total = descriptions.reduce(
    (sum, desc) => sum + (Number(desc?.expenses ?? desc?.value) || 0),
    0
  );

  if (useStoredTaskExpensesOnly) return total;

  let maxTripDuration = 0;
  descriptions.forEach((desc) => {
    if (desc?.tripduration) {
      maxTripDuration = Math.max(maxTripDuration, Number(desc.tripduration) || 0);
    }
  });

  if (maxTripDuration > 0) {
    total +=
      maxTripDuration * (Number(perdiem) || 0) +
      maxTripDuration * (Number(averagecost) || 0);
  }

  return total;
};

/**
 * Per-diem / average cost for installation on won leads in finance:
 * use rates frozen on the project at win time; never apply live salesperson settings to won leads.
 */
const resolveInstallationExpenseRatesForProject = (
  projectId,
  projectsList,
  otherExpensesList,
  financeDataProjectList = [],
  leadRow = null,
  financeFrozenMode = false,
  tasksForInference = null,
  personIdForInference = null
) => {
  const snapshot = findInstallationSnapshotProject(
    projectId,
    leadRow,
    projectsList,
    financeDataProjectList
  );

  if (hasFrozenInstallRates(snapshot)) {
    return {
      perdiem: Number(snapshot.perdiem) || 0,
      averagecost:
        Number(snapshot.avgcost ?? snapshot.averagecost) || 0,
      useStoredTaskExpensesOnly: false,
    };
  }

  if (financeFrozenMode || isWonLeadRow(leadRow)) {
    if (tasksForInference && personIdForInference) {
      const inferred = inferFrozenTripRatesFromTasks(
        tasksForInference,
        projectId,
        personIdForInference
      );
      if (inferred) return inferred;
    }
    return { perdiem: 0, averagecost: 0, useStoredTaskExpensesOnly: true };
  }

  const matchedProjectsForId = (projectsList || []).filter(
    (p) => String(p?._id || "") === String(projectId || "")
  );
  const installationRow =
    resolveInstallationProjectRowForTrainingRollup(
      [{ projectid: projectId }],
      matchedProjectsForId
    ) || matchedProjectsForId[0];

  const ownerUserId = resolveEntityId(installationRow?.createdBy);
  if (!ownerUserId) {
    return { perdiem: 0, averagecost: 0, useStoredTaskExpensesOnly: false };
  }

  const expensesForUser = scopeOtherExpensesForUser(otherExpensesList, {
    loggedUserId: ownerUserId,
  });
  const live = getPerdiemAndAverageCostFromExpenses(expensesForUser);
  return { ...live, useStoredTaskExpensesOnly: false };
};

const findProjectForFinanceEntry = (
  entry,
  typeService,
  projectsList,
  financeDataProjectList
) => {
  const matched =
    (projectsList || []).find(
      (p) => entry?.ProjectID && String(p._id) === String(entry.ProjectID)
    ) ||
    (projectsList || []).find(
      (p) =>
        p.ProjectName === entry?.ProjectName &&
        String(p.TypeService ?? "").trim() === String(typeService ?? "").trim()
    ) ||
    (financeDataProjectList || []).find(
      (p) => entry?.ProjectID && String(p._id) === String(entry.ProjectID)
    );

  return resolveProjectRatesSource(matched, projectsList);
};

const mapManagerAssignedRatesToQuotationDetails = (sourceProject) =>
  (sourceProject?.ManagerAssignedRates || []).map((rate) => ({
    designation: rate.designation ?? "",
    personid: rate.personid,
    hourlysalaray: Number(rate.hourlyrate) || 0,
    overheadpercentage: 0,
  }));

const mapInstallationQuotationsToDetails = (quotes, ratesOwnerId) =>
  (quotes || []).map((q) => ({
    designation: q.designation ?? "",
    personid: q.personid,
    hourlysalaray: Number(q.hourlysalaray) || 0,
    overheadpercentage: Number(q.overheadpercentage) || 0,
    createdBy: ratesOwnerId ?? resolveEntityId(q.createdBy),
  }));

/** Won-lead finance: frozen hourly rates from project snapshot; person ids from assigned persons / quotations. */
const resolveFrozenQuotationDetailsForFinance = (
  sourceProject,
  typeService,
  quotationsList,
  salesPersonId,
  employeesList = []
) => {
  const typeNorm = String(typeService || sourceProject?.TypeService || "")
    .toLowerCase()
    .trim();

  // Installation: hourly rates in Settings are owned by the currently assigned manager.
  // After a manager change in project approved, use that manager's rates — not frozen snapshot.
  if (typeNorm.includes("installation")) {
    const currentManagerId = resolveCurrentInstallationManagerId(
      sourceProject,
      employeesList
    );
    if (currentManagerId) {
      const installQuotes = (quotationsList || []).filter((q) => {
        const des = String(q?.designation || "").toLowerCase();
        return (
          des.includes("installation") &&
          String(resolveEntityId(q.createdBy)) === String(currentManagerId)
        );
      });
      if (installQuotes.length > 0) {
        return mapInstallationQuotationsToDetails(installQuotes, currentManagerId);
      }
    }
  }

  const fromAssigned = mapManagerAssignedRatesToQuotationDetails(sourceProject);
  if (fromAssigned.length > 0) return fromAssigned;

  const managerRate = Number(sourceProject?.managerHourlyRate) || 0;
  const operationRate = Number(sourceProject?.operationHourlyRate) || 0;

  const getManagerIdsForProject = () => {
    const assignedManagerId = resolveCurrentInstallationManagerId(
      sourceProject,
      employeesList
    );
    if (assignedManagerId) return [assignedManagerId];

    const raw = sourceProject?.managerid ?? sourceProject?.managerId;
    const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
    return list.map((id) => resolveEntityId(id)).filter(Boolean);
  };

  const scopedQuotes = (quotationsList || []).filter((q) => {
    const des = String(q?.designation || "").toLowerCase();
    if (!typeNorm || !des.includes(typeNorm)) return false;

    if (typeNorm.includes("installation")) {
      const managerIds = getManagerIdsForProject();
      if (managerIds.length) {
        return managerIds.includes(String(resolveEntityId(q.createdBy)));
      }
      return true;
    }

    if (salesPersonId) {
      return String(resolveEntityId(q.createdBy)) === String(salesPersonId);
    }
    return true;
  });

  const getPersonIdFromAssigned = (kind) => {
    const assigned = sourceProject?.Assignedperson || [];
    for (const ap of assigned) {
      const emp = (employeesList || []).find(
        (e) =>
          e.EmpEmail &&
          ap.email &&
          e.EmpEmail.toLowerCase() === ap.email.toLowerCase()
      );
      const personId = resolveEntityId(ap.personid ?? ap.personId ?? emp?._id);
      if (!personId) continue;

      const roles = emp?.customRoles || [];
      const isMgr = roles.some((role) =>
        String(role || "").toLowerCase().includes("manager")
      );
      if (kind === "manager" && isMgr) return personId;
      if (kind === "operations" && !isMgr) return personId;
    }
    return null;
  };

  const pickPersonQuote = (kind) => {
    if (kind === "manager") {
      return scopedQuotes.find((q) => {
        const des = String(q.designation || "").toLowerCase();
        return (
          (des.includes("project manager") ||
            (des.includes("manager") && !des.includes("operations"))) &&
          !des.includes("employee")
        );
      });
    }
    return scopedQuotes.find((q) => {
      const des = String(q.designation || "").toLowerCase();
      return (
        (des.includes("operations") || des.includes("employee")) &&
        !des.includes("manager")
      );
    });
  };

  const frozen = [];
  const mgrQuote = pickPersonQuote("manager");
  const empQuote = pickPersonQuote("operations");
  const mgrPersonId =
    getPersonIdFromAssigned("manager") ||
    resolveEntityId(mgrQuote?.personid);
  const empPersonId =
    getPersonIdFromAssigned("operations") ||
    resolveEntityId(empQuote?.personid);

  if (managerRate > 0 && mgrPersonId) {
    frozen.push({
      designation: mgrQuote?.designation || `${typeService} Project manager`,
      personid: mgrPersonId,
      hourlysalaray: managerRate,
      overheadpercentage: 0,
    });
  }
  if (operationRate > 0 && empPersonId) {
    frozen.push({
      designation: empQuote?.designation || `${typeService} Operations`,
      personid: empPersonId,
      hourlysalaray: operationRate,
      overheadpercentage: 0,
    });
  }
  if (frozen.length > 0) return frozen;

  return scopedQuotes.map((q) => ({
    designation: q.designation ?? "",
    personid: q.personid,
    hourlysalaray: Number(q.hourlysalaray) || 0,
    overheadpercentage: Number(q.overheadpercentage) || 0,
  }));
};

/**
 * Bake installation trip (per diem + average cost) into task expenses using the
 * project's salesperson rates so SalesData totals stay correct without global rates.
 */
const normalizeInstallationTasksForDisplay = (
  tasks,
  projectsList,
  otherExpensesList,
  financeDataProjectList = [],
  leadRow = null,
  financeFrozenMode = false
) => {
  if (!tasks?.length) return tasks;

  const byPersonProject = new Map();

  tasks.forEach((task, index) => {
    if (!isInstallationTypeService(task?.typeservice)) return;
    const projectId = task?.projectid;
    const personId = task?.taskownerid;
    if (!projectId || !personId) return;
    const key = `${projectId}|${personId}`;
    if (!byPersonProject.has(key)) byPersonProject.set(key, []);
    byPersonProject.get(key).push({ task, index });
  });

  if (!byPersonProject.size) return tasks;

  const result = tasks.map((t) => ({ ...t }));

  byPersonProject.forEach((entries, key) => {
    const [projectId, personId] = key.split("|");
    const {
      perdiem: projectPerdiem,
      averagecost: projectAveragecost,
      useStoredTaskExpensesOnly,
    } = resolveInstallationExpenseRatesForProject(
      projectId,
      projectsList,
      otherExpensesList,
      financeDataProjectList,
      leadRow,
      financeFrozenMode,
      tasks,
      personId
    );

    if (useStoredTaskExpensesOnly) return;

    const correctTotal = sumInstallationOtherExpensesForPerson(
      tasks,
      projectId,
      personId,
      projectPerdiem,
      projectAveragecost,
      useStoredTaskExpensesOnly
    );

    const adjustment =
      correctTotal -
      entries.reduce(
        (sum, e) => sum + (Number(e.task?.expenses ?? e.task?.value) || 0),
        0
      );

    entries.forEach((e, i) => {
      const idx = e.index;
      const base = Number(result[idx]?.expenses ?? result[idx]?.value) || 0;
      result[idx] = {
        ...result[idx],
        tripduration: 0,
        expenses: i === 0 ? base + adjustment : base,
      };
    });
  });

  return result;
};

/** Same installation task normalization as the detail table (SalesData / FinanceData). */
const buildNormalizedTasksForLeadRow = ({
  row,
  rowKey,
  tasksSource,
  financeDataProject,
  projects,
  otherExpenses,
  employees,
  quotations,
  isFinanceOrAccountingUser,
}) => {
  const matchedProjects = (financeDataProject || []).filter(
    (proj) => getRowKey(proj) === rowKey
  );
  const projectIds = matchedProjects.map((p) => p._id).filter(Boolean);
  if (!projectIds.length || !tasksSource?.length) return tasksSource || [];

  let nextTasks = tasksSource;

  projectIds.forEach((projectId) => {
    const projectRow =
      matchedProjects.find((p) => p._id === projectId) ||
      financeDataProject.find((p) => p._id === projectId);
    const salesperson = projectRow
      ? employees.find((emp) => emp.EmpEmail === projectRow.SalesPersonEmail)
      : null;
    const installationManagerId = resolveCurrentInstallationManagerId(
      projectRow,
      employees
    );
    const { managerId, employeeId } = resolveInstallationPersonIdsFromQuotations(
      quotations,
      salesperson?._id,
      installationManagerId
    );
    if (!managerId || !employeeId) return;

    const {
      perdiem: managerPerdiem,
      averagecost: managerAveragecost,
      useStoredTaskExpensesOnly: managerStoredOnly,
    } = resolveInstallationExpenseRatesForProject(
      projectId,
      projects,
      otherExpenses,
      financeDataProject,
      row,
      isFinanceOrAccountingUser,
      nextTasks,
      managerId
    );
    const {
      perdiem: employeePerdiem,
      averagecost: employeeAveragecost,
      useStoredTaskExpensesOnly: employeeStoredOnly,
    } = resolveInstallationExpenseRatesForProject(
      projectId,
      projects,
      otherExpenses,
      financeDataProject,
      row,
      isFinanceOrAccountingUser,
      nextTasks,
      employeeId
    );

    const managerTotal = sumInstallationOtherExpensesForPerson(
      nextTasks,
      projectId,
      managerId,
      managerPerdiem,
      managerAveragecost,
      managerStoredOnly
    );
    const employeeTotal = sumInstallationOtherExpensesForPerson(
      nextTasks,
      projectId,
      employeeId,
      employeePerdiem,
      employeeAveragecost,
      employeeStoredOnly
    );

    if (managerTotal < employeeTotal) {
      nextTasks = swapInstallationDescriptionOwners(
        nextTasks,
        projectId,
        managerId,
        employeeId
      );
    }
  });

  return normalizeInstallationTasksForDisplay(
    nextTasks,
    projects,
    otherExpenses,
    financeDataProject,
    row,
    isFinanceOrAccountingUser
  );
};

const normalizeFilteredQuotationInstallation = (
  filteredQuotation,
  quotationsList,
  employeesList
) => {
  if (!filteredQuotation) return filteredQuotation;

  const result = {};
  Object.entries(filteredQuotation).forEach(([projectKey, typeServices]) => {
    result[projectKey] = {};
    Object.entries(typeServices || {}).forEach(([typeService, items]) => {
      if (!isInstallationTypeService(typeService)) {
        result[projectKey][typeService] = items;
        return;
      }

      result[projectKey][typeService] = (items || []).map((item) => {
        const installationManagerId = resolveCurrentInstallationManagerId(
          {
            AssignedManagerEmail: item?.AssignedManagerEmail,
            Assignedperson: item?.Assignedperson,
          },
          employeesList
        );
        const { managerId, employeeId } = resolveInstallationPersonIdsFromQuotations(
          quotationsList,
          item?.SalesPersonId,
          installationManagerId
        );
        if (!managerId || !employeeId) return item;

        const workEntries = (item.WorkEntries || []).map((entry) => ({
          ...entry,
          createdBy: {
            ...entry.createdBy,
            role: normalizeInstallationDisplayRoles(
              entry?.createdBy?.role || [],
              entry?.taskownerid,
              managerId,
              employeeId
            ),
          },
        }));

        const workOwnerIds = new Set(
          workEntries.map((e) => String(e?.taskownerid || ""))
        );

        (item.Assignedperson || []).forEach((assignedPerson) => {
          const personEmail = assignedPerson?.email;
          const employee = (employeesList || []).find(
            (emp) =>
              emp.EmpEmail &&
              personEmail &&
              emp.EmpEmail.toLowerCase() === personEmail.toLowerCase()
          );
          const personId = employee?._id;
          if (
            !personId ||
            workOwnerIds.has(String(personId)) ||
            (String(personId) !== String(managerId) &&
              String(personId) !== String(employeeId))
          ) {
            return;
          }

          workEntries.push({
            taskownerid: personId,
            hoursspent: 0,
            createdBy: {
              email: employee.EmpEmail || "",
              name: `${employee.EmpFirstName || ""} ${employee.EmpLastName || ""}`.trim(),
              role: normalizeInstallationDisplayRoles(
                employee.customRoles || [],
                personId,
                managerId,
                employeeId
              ),
            },
            projectid: item.ProjectID,
            TypeService: item.TypeService,
          });
          workOwnerIds.add(String(personId));
        });

        return { ...item, WorkEntries: workEntries };
      });
    });
  });

  return result;
};

const swapInstallationDescriptionOwners = (
  tasks,
  projectId,
  managerId,
  employeeId
) => {
  if (!managerId || !employeeId || String(managerId) === String(employeeId)) {
    return tasks;
  }

  return (tasks || []).map((task) => {
    if (String(task?.projectid || "") !== String(projectId)) return task;
    if (!isInstallationTypeService(task?.typeservice)) return task;

    if (String(task?.taskownerid || "") === String(managerId)) {
      return { ...task, taskownerid: employeeId };
    }
    if (String(task?.taskownerid || "") === String(employeeId)) {
      return { ...task, taskownerid: managerId };
    }
    return task;
  });
};

const ActualFinanceView = ({
  searchedLeadReports,
  showTable,
  searchedLeadReportswithoutyears,
  selectedYears = [],
}) => {
  const dispatch = useDispatch();
  const currency = useCurrencySymbol();
  const USAComma = CommaConvert();
  const { session } = useSelector((state) => state.session);
  const userLevel = session?.auth || localStorage.getItem("auth") || "";
  const isFinanceOrAccountingUser = userLevel === "L2" || userLevel === "L5";
  const formatOtherExpenseComma = useMemo(
    () => (isFinanceOrAccountingUser ? createOtherExpenseCommaFormatter() : undefined),
    [isFinanceOrAccountingUser]
  );
  const formatTotalExpenseComma = useMemo(
    () => (isFinanceOrAccountingUser ? createTotalExpenseCommaFormatter() : undefined),
    [isFinanceOrAccountingUser]
  );
  const { enqueueSnackbar } = useSnackbar();
  const [calculationTable, setCalculationTable] = useState(false);
  const [TableData, setTableData] = useState([]);
  const [selectedProjectName, setSelectedProjectName] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState([]);
  const [filteredQuotation, setFilteredQuotation] = useState(null);
  const [GandATax, setGandATax] = useState(15);
  const [taxValue, setTaxValue] = useState(27);
  const { taxes } = useSelector((state) => state.salesTaxes);
  const { projects } = useSelector((state) => state.projects);
  const { group } = useSelector((state) => state.group);
  const { profiles } = useSelector((state) => state.companyProfile);
  // const { quotations } = useSelector((state) => state.hourlyRateQuotation);
  const { employees } = useSelector((state) => state.employees);
  const { items: otherExpenses } = useSelector((state) => state.otherExpenses);
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [quotedValues, setQuotedValues] = useState({});
  const AllServiceTableRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  const [selectedTab, setSelectedTab] = useState("");
  const [financeDataProject, setfinanceDataProject] = useState([]);
  const [comments, setComments] = useState({});
  const { projectTrackingWorkEntries, entries } = useSelector(
    (state) => state.workentries
  );
  const { hardwareProjectTracking, items: hardwareItems } = useSelector(
    (state) => state.hardwareDescription
  );
  const { descriptionProjectTracking, tasks: descriptionTasksRegular } = useSelector(
    (state) => state.descriptionTasks
  );
  const [popupProject, setPopupProject] = useState(null);
  // const { data } = useSelector(
  //     (state) => state.salesHourlyRate
  // );

  const resolveId = (value) => {
    if (!value) return "";
    if (typeof value === "string" || typeof value === "number") {
      return String(value).trim();
    }
    return String(value?._id || value?.id || "").trim();
  };

  const getGroupIdFromRow = (row) =>
    resolveId(row?.groupid) ||
    resolveId(row?.projectdata?.groupid) ||
    resolveId(row?.lead?.groupid) ||
    resolveId(row?.project?.groupid);

  const getProjectGroupIdFromRow = (row) => {
    const rowQuotationId = resolveId(row?.quotationId);
    const rowProjectName = String(row?.ProjectName || "").trim().toLowerCase();

    const matchedProject = projects.find((proj) => {
      const projQuotationId = resolveId(proj?.quotationId);
      if (rowQuotationId && projQuotationId) {
        return projQuotationId === rowQuotationId;
      }
      return String(proj?.ProjectName || "").trim().toLowerCase() === rowProjectName;
    });

    return resolveId(matchedProject?.groupid);
  };

  const getGroupNameById = (groupId) => {
    const resolvedGroupId = resolveId(groupId);
    if (!resolvedGroupId) return "";
    const matchedGroup = group.find(
      (grp) => resolveId(grp?._id || grp?.id) === resolvedGroupId
    );
    return matchedGroup?.newGroupName || matchedGroup?.name || "";
  };

  const getGroupDisplayName = (row) =>
    getGroupNameById(getGroupIdFromRow(row) || getProjectGroupIdFromRow(row)) ||
    row?.AssignedGroup ||
    row?.projectdata?.AssignedGroup ||
    "";

  const { quotations } = useSelector((state) => state.hourlyRateQuotation);
  console.log("selectedServiceTypesselectedServiceTypes", selectedServiceTypes);
  useEffect(() => {
    dispatch(fetchWorkEntriesProjectTracking());
    dispatch(fetchDescriptionTasksProjectTracking());
    dispatch(fetchHardwareProjectTracking());
    dispatch(fetchEmployees());
    dispatch(fetchSalesHourlyRates());
  }, [dispatch]);
  useEffect(() => {
    dispatch(fetchEmployees());
    dispatch(fetchWorkEntries());
    dispatch(fetchAllProjects());
    dispatch(fetchHardwareDescriptions());
    dispatch(fetchDescriptionTasks());
    dispatch(fetchQuotations());
    dispatch(fetchOtherExpenses());
    dispatch(fetchSalesTaxes());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchAllFinancial());
    dispatch(fetchSalesTaxes());
    dispatch(fetchAllProjects());
    dispatch(setOtheeExpensesData());
    dispatch(fetchCompanyProfiles());
    dispatch(fetchOtherExpenses());
  }, [dispatch]);

  // Normalize package: "Packages Switch CRM", "CRM", etc. -> treat as CRM for finance data visibility
  const pkgNormalized = String(profiles?.[0]?.subscriptionpackage || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  const isCrmPackage =
    pkgNormalized === "crm" ||
    pkgNormalized.includes("crm") ||
    (pkgNormalized.length > 0 && !pkgNormalized.includes("erp"));

  useEffect(() => {
    // When package is CRM: show quoted projects (isProjecttracking=false) - CRM has no actual tracking
    // When package is ERP: show actual/tracking projects (isProjecttracking=true)
    const isProjecttracking = !isCrmPackage;

    const filteredProject = projects.filter(
      (proj) => proj.isProjecttracking === isProjecttracking
    );

    const qualifiedGroups = filteredProject.filter((project) => {
      // Must have at least one assigned person
      if (!project?.Assignedperson?.length) return false;

      if (isCrmPackage) {
        // CRM: quoted project statuses (same as SalesFinaceFilter)
        return project.Assignedperson.some(
          (person) =>
            (person.salespersonaction === "accept" ||
              person.salespersonaction === "project not-assigned") &&
            (person.salespersonstatus === "response received" ||
              person.salespersonstatus === "project approved" ||
              person.salespersonstatus === "project not-approved" ||
              person.salespersonstatus === "project assigned" ||
              person.salespersonstatus === "project not-assigned")
        );
      }

      // ERP: actual/tracking - at least one assigned person must have ongoing/complete
      return project.Assignedperson.some(
        (person) =>
          person.salespersonstatus === "project ongoing" ||
          person.salespersonstatus === "project complete"
      );
    });
    setfinanceDataProject(qualifiedGroups);
  }, [projects, isCrmPackage]);

  useEffect(() => {
    const projectsGroupedByKey = financeDataProject.reduce((acc, project) => {
      const key = getRowKey(project);
      const existingGroup = acc.find((g) => g.key === key);
      if (existingGroup) {
        existingGroup.Projects.push(project);
      } else {
        acc.push({
          key,
          ProjectName: project.ProjectName,
          Projects: [project],
        });
      }
      return acc;
    }, []);

    if (searchedLeadReports && searchedLeadReports.length > 0) {
      // CRM package: show all searched leads (won) so L2 with all groups sees data; expense columns show 0 when no project match
      if (isCrmPackage) {
        setTableData(searchedLeadReports);
      } else {
        const validRowKeys = projectsGroupedByKey.map((group) => group.key);
        const filteredLeads = searchedLeadReports.filter((lead) =>
          validRowKeys.includes(getRowKey(lead))
        );
        setTableData(filteredLeads);
      }
    } else {
      setTableData([]);
    }
  }, [searchedLeadReports, financeDataProject, isCrmPackage]);

  const refsMap = {
    AllServiceTable: AllServiceTableRef,
  };
  const handleTabClick = (tabKey) => {
    setSelectedTab(tabKey);
    setTimeout(() => {
      refsMap[tabKey]?.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100); // slight delay so component mounts first
  };
  const [matchedProjects, setMatchedProjects] = useState([]); // always an array
  const [matchedEntries, setMatchedEntries] = useState([]); // ✅ will store matched entries

  useEffect(() => {
    const selectedKey = getRowKey(selectedProjectName);
    if (selectedKey && financeDataProject?.length) {
      const matches = financeDataProject.filter((proj) => getRowKey(proj) === selectedKey);
      setMatchedProjects(matches);
    } else {
      setMatchedProjects([]);
    }
  }, [selectedProjectName, financeDataProject]);

  const workEntriesSource = isCrmPackage ? entries : projectTrackingWorkEntries;

  useEffect(() => {
    if (matchedProjects.length && workEntriesSource.length) {
      const matchedIds = matchedProjects.map((proj) => proj._id);
      const filteredEntries = workEntriesSource.filter((entry) =>
        matchedIds.includes(entry.projectid)
      );

      const matchedPersons = matchedProjects.flatMap((project) =>
        (project.Assignedperson || []).map((person) => ({
          ...person,
          projectid: project._id,
          ProjectName: project.ProjectName, // optional
        }))
      );

      const matchedEmployees = employees.filter((employee) =>
        matchedPersons.some(
          (person) =>
            person.email?.trim().toLowerCase() ===
            employee.EmpEmail?.trim().toLowerCase()
        )
      );

      const matchedPeopleEntries = filteredEntries.filter((entry) =>
        matchedEmployees.some(
          (employee) => employee._id?.trim() === entry.taskownerid?.trim()
        )
      );

      setMatchedEntries(matchedPeopleEntries);
    } else {
      setMatchedEntries([]);
    }
  }, [matchedProjects, workEntriesSource, employees]);

  const [matchedHardwareEntries, setMatchedHardwareEntries] = useState([]); // ✅ will store matched entries

  const hardwareSource = isCrmPackage ? hardwareItems : hardwareProjectTracking;
  const tasksSource = isCrmPackage ? descriptionTasksRegular : descriptionProjectTracking;

  useEffect(() => {
    if (matchedProjects.length && hardwareSource.length) {
      const matchedIds = matchedProjects.map((proj) => proj._id);
      const filteredEntries = hardwareSource.filter((entry) =>
        matchedIds.includes(entry.projectid)
      );

      const matchedPersons = matchedProjects.flatMap((project) =>
        (project.Assignedperson || []).map((person) => ({
          ...person,
          projectid: project._id,
          ProjectName: project.ProjectName, // optional
        }))
      );

      const matchedEmployees = employees.filter((employee) =>
        matchedPersons.some(
          (person) =>
            person.email?.trim().toLowerCase() ===
            employee.EmpEmail?.trim().toLowerCase()
        )
      );

      const matchedPeopleEntries = filteredEntries.filter((entry) =>
        matchedEmployees.some(
          (employee) => employee._id?.trim() === entry.taskownerid?.trim()
        )
      );

      setMatchedHardwareEntries(matchedPeopleEntries);
    } else {
      setMatchedHardwareEntries([]);
    }
  }, [matchedProjects, hardwareSource, employees]);

  useEffect(() => {
    if (matchedHardwareEntries.length) {
      setMatchedEntries((prevEntries) => [
        ...prevEntries,
        ...matchedHardwareEntries,
      ]);
    }
  }, [matchedHardwareEntries]);

  const [groupedProj, setGroupedProj] = useState({});
  const [finalMappedEntries, setFinalMappedEntries] = useState([]);

  useEffect(() => {
    if (matchedEntries.length && employees.length) {
      const mapped = matchedEntries.map((entry) => {
        const matchedEmp = employees.find(
          (emp) => emp._id === entry.taskownerid
        );

        // Check if this is a hardware entry (has hardware-specific properties)
        const isHardwareEntry =
          entry.hasOwnProperty("assemblepartdescription") ||
          entry.hasOwnProperty("partno") ||
          entry.hasOwnProperty("quantity");

        if (isHardwareEntry) {
          // Map hardware entries
          return {
            createdBy: {
              email: matchedEmp?.EmpEmail || "",
              name: `${matchedEmp?.EmpFirstName || ""} ${
                matchedEmp?.EmpLastName || ""
              }`,
              role: matchedEmp?.customRoles || [],
            },
            assemblepartdescription: entry.assemblepartdescription,
            partno: entry.partno,
            quantity: entry.quantity,
            unitprice: entry.unitprice,
            totalprice: entry.totalprice,
            permonth: entry.permonth,
            taskstatus: entry.taskstatus,
            _id: entry._id,
            taskownerid: entry.taskownerid,
            TypeService: entry.typeservice || "Hardware", // Use the existing typeservice field
            projectid: entry.projectid,
          };
        } else {
          const projectForEntry = projects?.find((p) => p._id === entry.projectid);
          const salesperson = projectForEntry
            ? employees.find((emp) => emp.EmpEmail === projectForEntry.SalesPersonEmail)
            : null;
          const installationManagerId = resolveCurrentInstallationManagerId(
            projectForEntry,
            employees
          );
          const { managerId, employeeId } = resolveInstallationPersonIdsFromQuotations(
            quotations,
            salesperson?._id,
            installationManagerId
          );
          const displayRoles =
            isInstallationTypeService(entry.typeservice) && managerId && employeeId
              ? normalizeInstallationDisplayRoles(
                  matchedEmp?.customRoles || [],
                  entry.taskownerid,
                  managerId,
                  employeeId
                )
              : matchedEmp?.customRoles || [];

          // Map software/cloud/installation entries (your existing logic)
          return {
            createdBy: {
              email: matchedEmp?.EmpEmail || "",
              name: `${matchedEmp?.EmpFirstName || ""} ${
                matchedEmp?.EmpLastName || ""
              }`,
              role: displayRoles,
            },
            task: entry.task,
            hoursassigned: entry.hoursassigned,
            hoursspent: entry.hoursspent,
            taskStatus: entry.taskStatus,
            notes: entry.notes,
            _id: entry._id,
            taskownerid: entry.taskownerid,
            TypeService: entry.typeservice || matchedEmp?.servicetype,
            projectid: entry.projectid,
          };
        }
      });

      setFinalMappedEntries(mapped);
    } else {
      setFinalMappedEntries([]);
    }
  }, [matchedEntries, employees, projects, quotations]);

  useEffect(() => {
    if (!projects || projects.length === 0 || !employees) return;
    const grouped = {};
    const projectKey = (p) =>
      p?.quotationId != null && String(p.quotationId).trim() !== ""
        ? String(p.quotationId).trim()
        : p?.ProjectName ?? "";
    projects.forEach((proj) => {
      const key = projectKey(proj);
      const typeService = proj.TypeService;
      const projectId = proj._id;

      const matchedEntries = finalMappedEntries.filter(
        (entry) =>
          entry.TypeService === typeService && entry.projectid === projectId
      );

      if (matchedEntries.length === 0) return;
      const salesperson = employees.find(
        (emp) => emp.EmpEmail === proj.SalesPersonEmail
      );
      const filteredProject = {
        TypeService: proj.TypeService,
        ResourceType: proj.ResourceType,
        ProjectName: proj.ProjectName,
        QuotedValue: proj.ServiceValue,
        WorkEntries: matchedEntries,
        groupid: proj.groupid,
        AssignedManagerEmail: proj.AssignedManagerEmail,
        Assignedperson: proj.Assignedperson,
        ProjectID: proj._id,
        ProjectNotes: proj?.ProjectFinanceNotes,
        SalesPersonId: salesperson ? salesperson._id : null,
      };

      if (!grouped[key]) grouped[key] = {};
      if (!grouped[key][typeService]) grouped[key][typeService] = [];
      grouped[key][typeService].push(filteredProject);
    });

    setGroupedProj(grouped);
  }, [projects, employees, finalMappedEntries]);

  const [matchedQuotations, setMatchedQuotations] = useState({});
  useEffect(() => {
    if (!groupedProj) return;
    if (!isFinanceOrAccountingUser && !quotations) return;

    const updatedGroupedProj = {};
    // Loop through each project
    Object.entries(groupedProj).forEach(([projectKey, serviceTypes]) => {
      updatedGroupedProj[projectKey] = {};
      const sampleEntry = Object.values(serviceTypes || {})
        .flat()
        .find(Boolean);
      const leadRow = {
        quotationId: projectKey,
        ProjectName: sampleEntry?.ProjectName || projectKey,
        Status: "won",
        isProjectQuoted: true,
      };

      // Loop through each TypeService within that project
      Object.entries(serviceTypes).forEach(([typeService, serviceArray]) => {
        // Keep the original service array
        const updatedServiceArray = serviceArray.map((entry) => {
          if (isFinanceOrAccountingUser) {
            const sourceProject = findProjectForFinanceEntry(
              entry,
              typeService,
              projects,
              financeDataProject
            );
            const isHardware = String(typeService ?? "")
              .toLowerCase()
              .includes("hardware");
            const installRates = isInstallationTypeService(typeService)
              ? resolveInstallationExpenseRatesForProject(
                  entry.ProjectID,
                  projects,
                  otherExpenses,
                  financeDataProject,
                  leadRow,
                  true
                )
              : null;

            return {
              ...entry,
              quotationDetails: isHardware
                ? []
                : resolveFrozenQuotationDetailsForFinance(
                    sourceProject,
                    typeService,
                    quotations,
                    entry.SalesPersonId,
                    employees
                  ),
              projectPerdiem:
                installRates?.perdiem ??
                (Number(sourceProject?.perdiem) || 0),
              projectAvgcost:
                installRates?.averagecost ??
                (Number(
                  sourceProject?.avgcost ?? sourceProject?.averagecost
                ) || 0),
            };
          }

          // Match quotations where designation includes TypeService
          const matchedQuotationsList = quotations.filter((q) => {
            const matchesType = q.designation
              ?.toLowerCase()
              .includes(typeService.toLowerCase());
            if (!matchesType) return false;
            if (entry.SalesPersonId) {
              return String(q.createdBy || "") === String(entry.SalesPersonId);
            }
            return true;
          });
          return {
            ...entry,
            quotationDetails: matchedQuotationsList, // add new key with matched quotations
          };
        });

        updatedGroupedProj[projectKey][typeService] = updatedServiceArray;
      });
    });

    setMatchedQuotations(updatedGroupedProj);
  }, [
    quotations,
    groupedProj,
    isFinanceOrAccountingUser,
    projects,
    financeDataProject,
    employees,
    otherExpenses,
  ]);
  useEffect(() => {
    const rowKey = getRowKey(selectedProjectName);
    if (!rowKey || !matchedQuotations) return;
    const match = matchedQuotations[rowKey] ?? matchedQuotations[selectedProjectName?.ProjectName];
    if (match) {
      setFilteredQuotation({ [rowKey]: match });
    } else {
      setFilteredQuotation(null);
    }
  }, [selectedProjectName, matchedQuotations]);

  const financeFilteredQuotation = useMemo(
    () =>
      normalizeFilteredQuotationInstallation(
        filteredQuotation,
        quotations,
        employees
      ),
    [filteredQuotation, quotations, employees]
  );

  const financeDetailTasks = useMemo(() => {
    if (!tasksSource?.length || !calculationTable) return tasksSource;

    const rowKey = getRowKey(selectedProjectName);
    if (!rowKey) return tasksSource;

    return buildNormalizedTasksForLeadRow({
      row: selectedProjectName,
      rowKey,
      tasksSource,
      financeDataProject,
      projects,
      otherExpenses,
      employees,
      quotations,
      isFinanceOrAccountingUser,
    });
  }, [
    tasksSource,
    calculationTable,
    selectedProjectName,
    financeDataProject,
    employees,
    quotations,
    projects,
    otherExpenses,
    isFinanceOrAccountingUser,
  ]);

  useEffect(() => {
    if (profiles?.[0]?.subscriptionpackage) {
      // Normalize to "CRM" or "ERP" for internal checks (showExpensesop, column headers)
      const pkg = String(profiles[0].subscriptionpackage).replace(/\s+/g, " ").trim().toLowerCase();
      setSelectedServiceTypes(pkg.includes("erp") && !pkg.includes("crm") ? "ERP" : "CRM");
    }
  }, [profiles]);

  const itemsPerPage = 10;
  const startIdx = (currentPage - 1) * itemsPerPage;
  const endIdx = startIdx + itemsPerPage;
  const currentItems = TableData?.slice(startIdx, endIdx);

  const totalItems = TableData?.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const sortAlphabetically = (targetToSort, order) => {
    const key = `${targetToSort}-${order}`;
    const sortedReports = [...TableData]; // Use TableData instead of searchedLeadReports

    switch (key) {
      case "projectname-az":
        sortedReports.sort((a, b) =>
          a.ProjectName?.trim()
            .toLowerCase()
            .localeCompare(b.ProjectName?.trim().toLowerCase())
        );
        break;

      case "projectname-za":
        sortedReports.sort((a, b) =>
          b.ProjectName?.trim()
            .toLowerCase()
            .localeCompare(a.ProjectName?.trim().toLowerCase())
        );
        break;

      case "groupname-az":
        sortedReports.sort((a, b) =>
          getGroupDisplayName(a)
            ?.trim()
            .toLowerCase()
            .localeCompare(getGroupDisplayName(b)?.trim().toLowerCase())
        );
        break;

      case "groupname-za":
        sortedReports.sort((a, b) =>
          getGroupDisplayName(b)
            ?.trim()
            .toLowerCase()
            .localeCompare(getGroupDisplayName(a)?.trim().toLowerCase())
        );
        break;

      case "clientname-az":
        sortedReports.sort((a, b) =>
          a.ClientName?.trim()
            .toLowerCase()
            .localeCompare(b.ClientName?.trim().toLowerCase())
        );
        break;

      case "clientname-za":
        sortedReports.sort((a, b) =>
          b.ClientName?.trim()
            .toLowerCase()
            .localeCompare(a.ClientName?.trim().toLowerCase())
        );
        break;

      default:
        console.error("Invalid Order");
    }

    setTableData(sortedReports);
  };

  const numericSort = (order) => {
    const sortedReports = [...TableData]; // Use TableData instead of searchedLeadReports

    order === "az"
      ? sortedReports.sort(
          (a, b) => Number(a.QuotedValue) - Number(b.QuotedValue)
        )
      : sortedReports.sort(
          (a, b) => Number(b.QuotedValue) - Number(a.QuotedValue)
        );

    setTableData(sortedReports);
  };
  useEffect(() => {
    dispatch(fetchLeads());
    dispatch(fetchGroups());
  }, [dispatch]);
  useEffect(() => {
    const userEmail = localStorage.getItem("email");
    const currentEmployee = employees.find((emp) => emp.EmpEmail === userEmail);
    const filteredTaxes = taxes.filter(
      (tax) => tax.createdBy === currentEmployee?._id
    );
    filteredTaxes?.forEach((data) => {
      if (data.taxName === "G&A Expenses") {
        setGandATax(data.taxPercentage);
      } else if (data.taxName === "Tax") {
        setTaxValue(data.taxPercentage);
      }
    });
  }, [taxes, employees]);

  const [searchedCalcProjects, setSearchedCalcProjects] = useState([]);

  useEffect(() => {
    if (!searchedLeadReports || !financeDataProject) return;
    const matchedProjects = financeDataProject.filter((row) =>
      searchedLeadReports.some((proj) => getRowKey(proj) === getRowKey(row))
    );
    setSearchedCalcProjects(matchedProjects);
  }, [searchedLeadReports, financeDataProject]);

  const [mergedProjects, setMergedProjects] = useState([]);

  useEffect(() => {
    if (!searchedCalcProjects) return;

    const newData = searchedCalcProjects.map((proj) => {
      const matchedEntries = workEntriesSource.filter(
        (entry) => entry.projectid === proj._id
      );

      const matchedDescriptions = tasksSource.filter(
        (task) => task.projectid === proj._id
      );

      const matchedHardware = hardwareSource.filter(
        (task) => task.projectid === proj._id
      );

      return {
        ProjectName: proj.ProjectName,
        quotationId: proj.quotationId ?? null,
        TaskList: matchedEntries,
        Description: matchedDescriptions,
        TypeService: proj.TypeService,
        HardwareList: matchedHardware,
        Assignedperson: proj.Assignedperson,
        SalesPersonEmail: proj.SalesPersonEmail,
      };
    });

    setMergedProjects(newData);
  }, [
    searchedCalcProjects,
    workEntriesSource,
    tasksSource,
    hardwareSource,
  ]);

  const [combinedProjects, setCombinedProjects] = useState([]);

  useEffect(() => {
    if (!mergedProjects || mergedProjects.length === 0) return;
    const projectKey = (p) =>
      p?.quotationId != null && String(p.quotationId).trim() !== ""
        ? String(p.quotationId).trim()
        : p?.ProjectName ?? "";
    const grouped = Object.values(
      mergedProjects.reduce((acc, project) => {
        const key = projectKey(project);
        if (!acc[key]) {
          acc[key] = {
            ProjectName: project.ProjectName,
            quotationId: project.quotationId ?? null,
            salespersonEmail: project.SalesPersonEmail,
            TaskList: [],
            Description: [],
            HardwareList: [],
          };
        }
        acc[key].TaskList.push(...(project.TaskList || []));
        acc[key].Description.push(...(project.Description || []));
        acc[key].HardwareList.push(...(project.HardwareList || []));
        return acc;
      }, {})
    );
    setCombinedProjects(grouped);
  }, [mergedProjects]);

  const [groupedProjects, setGroupedProjects] = useState([]);

  useEffect(() => {
    if (!combinedProjects?.length || !employees?.length) return;

    const updatedProjects = combinedProjects.map((project) => {
      const enrichWithDesignation = (arr = []) =>
        arr.map((item) => {
          // Prefer taskownerid first
          const emp =
            employees.find((e) => e._id === item.taskownerid) ||
            employees.find((e) => e._id === item.createdBy);

          if (emp) {
            // Only include designation if servicetype or customRoles exist
            const designationParts = [];
            if (emp.servicetype) designationParts.push(emp.servicetype);
            if (emp.customRoles?.length)
              designationParts.push(emp.customRoles[0]);

            return {
              ...item,
              designation: designationParts.join(" ").trim() || null,
            };
          }

          return { ...item, designation: null };
        });

      return {
        ...project,
        Description: enrichWithDesignation(project.Description),
        TaskList: enrichWithDesignation(project.TaskList),
        HardwareList: enrichWithDesignation(project.HardwareList),
      };
    });

    setGroupedProjects(updatedProjects);
  }, [combinedProjects, employees]);

  const [quotationProjects, setquotationProjects] = useState([]);

  console.log("quotationProjects", quotationProjects);
  useEffect(() => {
    if (quotationProjects?.length) {
      dispatch(setOtheeExpensesData(quotationProjects));
    }
  }, [dispatch, quotationProjects]);

  useEffect(() => {
    if (!groupedProjects || !employees) return;
    if (!isFinanceOrAccountingUser && !quotations) return;

    const updatedProjects = groupedProjects.map((project) => {
      const enrichTaskListWithQuotation = (taskList) =>
        (taskList || []).map((task) => {
          if (isFinanceOrAccountingUser) {
            if (!task.taskownerid) return task;

            const projectRows = financeDataProject
              .filter((p) => p.ProjectName === project.ProjectName)
              .map((p) => resolveProjectRatesSource(p, projects))
              .filter(Boolean);

            let matchedRate = null;
            for (const proj of projectRows) {
              matchedRate = (proj.ManagerAssignedRates || []).find(
                (rate) =>
                  String(rate.personid) === String(task.taskownerid) &&
                  (!task.designation ||
                    String(rate.designation || "").trim() ===
                      String(task.designation || "").trim())
              );
              if (matchedRate) break;
            }

            if (!matchedRate) {
              for (const proj of projectRows) {
                matchedRate = (proj.ManagerAssignedRates || []).find(
                  (rate) => String(rate.personid) === String(task.taskownerid)
                );
                if (matchedRate) break;
              }
            }

            if (matchedRate) {
              return {
                ...task,
                hourlysalaray: Number(matchedRate.hourlyrate) || 0,
                overheadpercentage: 0,
              };
            }

            const taskEmp = employees.find(
              (e) => String(e._id) === String(task.taskownerid)
            );
            const taskRoles = taskEmp?.customRoles || [];
            const isManagerRole = taskRoles.some((role) =>
              String(role || "").toLowerCase().includes("manager")
            );
            const projectRow = projectRows[0];
            const frozenHourly = isManagerRole
              ? Number(projectRow?.managerHourlyRate) || 0
              : Number(projectRow?.operationHourlyRate) || 0;
            if (frozenHourly > 0) {
              return {
                ...task,
                hourlysalaray: frozenHourly,
                overheadpercentage: 0,
              };
            }
            return task;
          }

          if (!task.designation) {
            return {
              ...task,
              hourlysalaray: task.hourlysalaray || 0,
              overheadpercentage: task.overheadpercentage || 0,
            };
          }

          const matchedQuote = quotations.find(
            (q) => q.designation === task.designation
          );

          return {
            ...task,
            hourlysalaray:
              (task.hourlysalaray ?? matchedQuote?.hourlysalaray ?? 0) || 0,
            overheadpercentage:
              (task.overheadpercentage ??
                matchedQuote?.overheadpercentage ??
                0) || 0,
          };
        });

      return {
        ...project,
        TaskList: enrichTaskListWithQuotation(project.TaskList),
        Description: project.Description || [],
        HardwareList: project.HardwareList || [],
      };
    });

    setquotationProjects(updatedProjects);
  }, [
    groupedProjects,
    quotations,
    employees,
    isFinanceOrAccountingUser,
    financeDataProject,
    projects,
  ]);

  const [, setInstallationDescriptions] = useState([]);

  useEffect(() => {
    if (!quotationProjects || quotationProjects.length === 0) return;

    const filteredProjects = quotationProjects
      .map((proj) => {
        const matchedDescriptions = (proj.Description || []).filter(
          (desc) =>
            desc.typeservice &&
            desc.typeservice.toLowerCase().includes("installation") &&
            desc.designation &&
            desc.designation.toLowerCase().includes("operations")
        );

        if (matchedDescriptions.length > 0) {
          return {
            ProjectName: proj.ProjectName,
            Description: matchedDescriptions,
          };
        }
        return null;
      })
      .filter(Boolean); // remove nulls

    setInstallationDescriptions(filteredProjects);
  }, [quotationProjects]);

  const [calculatedProjects, setCalculatedProjects] = useState([]);

  useEffect(() => {
    if (!quotationProjects || quotationProjects.length === 0) return;
    const newData = quotationProjects.map((proj) => {
      let taskValue = 0;
      let descriptionValue = 0;
      let hardwareValue = 0;

      if (proj.TaskList?.length > 0) {
        const ownerMap = {};

        proj.TaskList.forEach((task) => {
          const {
            taskownerid,
            hoursspent, // Use hoursspent instead of hoursassigned to match detailed calculation
            hourlysalaray,
            overheadpercentage,
          } = task;

          if (!taskownerid) return;

          if (!ownerMap[taskownerid]) {
            ownerMap[taskownerid] = {
              totalHours: hoursspent || 0,
              rate: (hourlysalaray || 0) + (overheadpercentage || 0),
            };
          } else {
            // Add hours if same owner
            ownerMap[taskownerid].totalHours += hoursspent || 0;
          }
        });

        // Now calculate per owner value and sum up
        Object.values(ownerMap).forEach((owner) => {
          taskValue += owner.totalHours * owner.rate;
        });
      }

      // ✅ Description calculation with Installation service condition
      if (proj.Description?.length > 0) {
        // Track max trip duration per project + owner (installation uses salesperson-scoped rates)
        const installationTripByProjectOwner = {};

        proj.Description.forEach((desc) => {
          if (
            desc.typeservice &&
            desc.typeservice.toLowerCase().includes("installation") &&
            desc.taskownerid &&
            desc.tripduration
          ) {
            const tripKey = `${desc.projectid || ""}|${desc.taskownerid}`;
            const tripVal = Number(desc.tripduration) || 0;
            installationTripByProjectOwner[tripKey] = Math.max(
              installationTripByProjectOwner[tripKey] || 0,
              tripVal
            );
          }

          if (desc.expenses) {
            descriptionValue += Number(desc.expenses) || 0;
          } else if (desc.value) {
            descriptionValue += Number(desc.value) || 0;
          }
        });

        const leadRow = {
          quotationId: proj.quotationId,
          ProjectName: proj.ProjectName,
          Status: "won",
          isProjectQuoted: true,
        };

        Object.entries(installationTripByProjectOwner).forEach(
          ([tripKey, tripDuration]) => {
            const [projectId] = tripKey.split("|");
            const {
              perdiem: projectPerdiem,
              averagecost: projectAveragecost,
              useStoredTaskExpensesOnly,
            } = resolveInstallationExpenseRatesForProject(
              projectId,
              projects,
              otherExpenses,
              financeDataProject,
              leadRow,
              isFinanceOrAccountingUser
            );
            if (useStoredTaskExpensesOnly) return;
            descriptionValue +=
              tripDuration * projectPerdiem + tripDuration * projectAveragecost;
          }
        );
      }

      // ✅ HardwareList calculation
      if (proj.HardwareList?.length > 0) {
        proj.HardwareList.forEach((hw) => {
          if (hw.totalprice) {
            hardwareValue += hw.totalprice;
          }
        });
      }

      return {
        ProjectName: proj.ProjectName,
        // Preserve quotationId so row keys match between calculatedProjects and table rows
        quotationId: proj.quotationId ?? null,
        TaskListValue: taskValue,
        DescriptionValue: descriptionValue,
        HardwareListValue: hardwareValue,
      };
    });

    setCalculatedProjects(newData);
  }, [
    quotationProjects,
    projects,
    otherExpenses,
    financeDataProject,
    isFinanceOrAccountingUser,
  ]);

  // When package is CRM, Quoted Expenses come from lead.quotedExpense; all other calculations (Gross Profit, G&A, Tax, Net) use this value.
  // When package is ERP, Actual Expenses in the main P&L table MUST equal the sum of
  // "Total Expenses" from the detailed Resources table (SalesData / FinanceData).
  const showExpensesop = (row) => {
    const rowKey = getRowKey(row);
    if (!rowKey) return "0";

    // CRM package: use lead.quotedExpense for Quoted Expenses (₹); parse to number for display and calculations
    if (isCrmPackage) {
      const raw = row?.quotedExpense;
      const num =
        typeof raw === "number"
          ? raw
          : parseFloat(String(raw || "").replace(/[^0-9.-]/g, "")) || 0;
      return num;
    }

    // For ERP, build filteredQuotation structure from financeDataProject (same format SalesData expects)
    // This ensures Actual Expenses equals the sum of "Total Expenses" from the detailed table
    // Build it the same way groupedProj does, but for this specific rowKey
    const matchedProjects = financeDataProject.filter((proj) => getRowKey(proj) === rowKey);
    
    if (!matchedProjects || matchedProjects.length === 0) {
      return "0";
    }

    // Build WorkEntries from workEntriesSource (same as finalMappedEntries logic)
    const projectIds = matchedProjects.map(p => p._id);
    const projectWorkEntries = workEntriesSource.filter(entry => projectIds.includes(entry.projectid));
    
    // Map work entries to match finalMappedEntries format
    const mappedWorkEntries = projectWorkEntries.map((entry) => {
      const matchedEmp = employees.find((emp) => emp._id === entry.taskownerid);
      return {
        createdBy: {
          email: matchedEmp?.EmpEmail || "",
          name: `${matchedEmp?.EmpFirstName || ""} ${matchedEmp?.EmpLastName || ""}`.trim(),
          role: matchedEmp?.customRoles || [],
        },
        task: entry.task,
        hoursassigned: entry.hoursassigned,
        hoursspent: entry.hoursspent,
        taskStatus: entry.taskStatus,
        notes: entry.notes,
        _id: entry._id,
        taskownerid: entry.taskownerid,
        TypeService: entry.typeservice || matchedEmp?.servicetype,
        projectid: entry.projectid,
      };
    });

    // Build grouped structure (same as groupedProj)
    const grouped = {};
    matchedProjects.forEach((proj) => {
      const key = getRowKey(proj);
      const typeService = proj.TypeService;
      const projectId = proj._id;

      const matchedEntries = mappedWorkEntries.filter(
        (entry) => entry.TypeService === typeService && entry.projectid === projectId
      );

      if (matchedEntries.length === 0 && !proj.Assignedperson?.length) return;
      
      const salesperson = employees.find((emp) => emp.EmpEmail === proj.SalesPersonEmail);
      const filteredProject = {
        TypeService: proj.TypeService,
        ResourceType: proj.ResourceType,
        ProjectName: proj.ProjectName,
        QuotedValue: proj.ServiceValue,
        WorkEntries: matchedEntries,
        groupid: proj.groupid,
        AssignedManagerEmail: proj.AssignedManagerEmail,
        Assignedperson: proj.Assignedperson,
        ProjectID: proj._id,
        ProjectNotes: proj?.ProjectFinanceNotes,
        SalesPersonId: salesperson ? salesperson._id : null,
      };

      if (!grouped[key]) grouped[key] = {};
      if (!grouped[key][typeService]) grouped[key][typeService] = [];
      grouped[key][typeService].push(filteredProject);
    });

    // Add quotationDetails (same as matchedQuotations logic)
    const filteredQuotationForRow = {};
    Object.entries(grouped).forEach(([projectName, serviceTypes]) => {
      filteredQuotationForRow[projectName] = {};
      Object.entries(serviceTypes).forEach(([typeService, serviceArray]) => {
        const updatedServiceArray = serviceArray.map((entry) => {
          if (isFinanceOrAccountingUser) {
            const sourceProject = findProjectForFinanceEntry(
              entry,
              typeService,
              projects,
              financeDataProject
            );
            const isHardware = String(typeService ?? "")
              .toLowerCase()
              .includes("hardware");
            const installRates = isInstallationTypeService(typeService)
              ? resolveInstallationExpenseRatesForProject(
                  entry.ProjectID,
                  projects,
                  otherExpenses,
                  financeDataProject,
                  row,
                  true
                )
              : null;

            return {
              ...entry,
              quotationDetails: isHardware
                ? []
                : resolveFrozenQuotationDetailsForFinance(
                    sourceProject,
                    typeService,
                    quotations,
                    entry.SalesPersonId,
                    employees
                  ),
              projectPerdiem:
                installRates?.perdiem ??
                (Number(sourceProject?.perdiem) || 0),
              projectAvgcost:
                installRates?.averagecost ??
                (Number(
                  sourceProject?.avgcost ?? sourceProject?.averagecost
                ) || 0),
            };
          }

          const matchedQuotations = quotations.filter((q) =>
            q.designation?.toLowerCase().includes(typeService.toLowerCase())
          );
          return {
            ...entry,
            quotationDetails: matchedQuotations,
          };
        });
        filteredQuotationForRow[projectName][typeService] = updatedServiceArray;
      });
    });

    const normalizedQuotationForRow = normalizeFilteredQuotationInstallation(
      filteredQuotationForRow,
      quotations,
      employees
    );
    const matchedQuotationData =
      normalizedQuotationForRow[rowKey] ??
      normalizedQuotationForRow[row?.ProjectName];

    if (!matchedQuotationData || Object.keys(matchedQuotationData).length === 0) {
      return "0";
    }

    const rowTasks = buildNormalizedTasksForLeadRow({
      row,
      rowKey,
      tasksSource,
      financeDataProject,
      projects,
      otherExpenses,
      employees,
      quotations,
      isFinanceOrAccountingUser,
    });

    const livePerdiem =
      otherExpenses?.find(
        (expense) =>
          expense.description &&
          expense.description.toLowerCase() === "perdiem"
      )?.cost || 0;
    const liveAveragecost =
      otherExpenses?.find(
        (expense) =>
          expense.description &&
          expense.description.toLowerCase() === "average cost"
      )?.cost || 0;

    // Helper functions (same as SalesData)
    const getTotalDescriptionValue = (
      projectID,
      personID,
      typeService,
      installRateOverrides
    ) => {
      const descriptions = rowTasks.filter(
        (desc) => desc.projectid === projectID && desc.taskownerid === personID
      );

      let totalValue = descriptions.reduce(
        (sum, desc) => sum + (Number(desc.expenses) || 0),
        0
      );

      if (typeService && typeService.toLowerCase().includes("installation")) {
        let maxTripDuration = 0;
        descriptions.forEach((desc) => {
          if (desc.tripduration) {
            maxTripDuration = Math.max(maxTripDuration, desc.tripduration);
          }
        });

        if (maxTripDuration > 0) {
          const installPerdiem =
            installRateOverrides?.perdiem ?? livePerdiem;
          const installAvgcost =
            installRateOverrides?.avgcost ?? liveAveragecost;
          totalValue +=
            maxTripDuration * installPerdiem +
            maxTripDuration * installAvgcost;
        }
      }

      return totalValue;
    };

    const getTotalHardwareCostValue = (projectID, personID, typeService) => {
      const hardwareItems = hardwareSource.filter(
        (hw) => hw.projectid === projectID && hw.typeservice === typeService
      );

      const hardwareByDescription = {};
      hardwareItems.forEach((hw) => {
        const description = hw.assemblepartdescription;
        if (!hardwareByDescription[description]) {
          hardwareByDescription[description] = hw;
        } else {
          if (Number(hw.totalprice) > Number(hardwareByDescription[description].totalprice)) {
            hardwareByDescription[description] = hw;
          }
        }
      });

      return Object.values(hardwareByDescription).reduce(
        (sum, hw) => sum + (Number(hw.totalprice) || 0),
        0
      );
    };

    // Process matchedQuotationData using the EXACT same logic as SalesData's transformFilteredQuotation
    let totalExpenses = 0;

    Object.values(matchedQuotationData).forEach((serviceArray) => {
      if (!Array.isArray(serviceArray)) return;
      
      serviceArray.forEach((item) => {
        // Create a map to track all persons (same as SalesData)
        const allPersonsMap = new Map();

        // Add persons from WorkEntries
        if (item?.WorkEntries?.length) {
          item.WorkEntries.forEach((entry) => {
            const personName = entry?.createdBy?.name;
            const personRoles = entry?.createdBy?.role || [];
            const personId = entry?.taskownerid;

            if (personName && personId) {
              if (!allPersonsMap.has(personName)) {
                allPersonsMap.set(personName, {
                  personId,
                  totalHours: entry.hoursspent || 0,
                  roles: personRoles,
                  fromWorkEntries: true,
                  email: entry?.createdBy?.email,
                });
              } else {
                const existing = allPersonsMap.get(personName);
                existing.totalHours += entry.hoursspent || 0;
              }
            }
          });
        }

        // Add persons from Assignedperson
        if (item?.Assignedperson?.length) {
          item.Assignedperson.forEach((assignedPerson) => {
            const personName = assignedPerson.name;
            const personEmail = assignedPerson.email;

            if (personName && personEmail) {
              const employee = employees.find(
                (emp) => emp.EmpEmail && emp.EmpEmail.toLowerCase() === personEmail.toLowerCase()
              );

              if (employee && !allPersonsMap.has(personName)) {
                allPersonsMap.set(personName, {
                  personId: employee._id || employee.id,
                  totalHours: 0,
                  roles: employee?.customRoles || [],
                  fromWorkEntries: false,
                  fromAssignedPerson: true,
                  email: personEmail,
                });
              }
            }
          });
        }

        if (allPersonsMap.size === 0) return;

        // Determine manager status
        const personManagerStatus = {};
        allPersonsMap.forEach((personData, name) => {
          personManagerStatus[name] = personData.roles.some((role) =>
            role.toLowerCase().includes("manager") || role.toLowerCase().includes("project manager")
          );
        });

        // Sort persons: Project Manager first, then Operations
        const sortedPersons = Array.from(allPersonsMap.entries()).sort(([nameA], [nameB]) => {
          const isManagerA = personManagerStatus[nameA];
          const isManagerB = personManagerStatus[nameB];
          if (isManagerA && !isManagerB) return -1;
          if (!isManagerA && isManagerB) return 1;
          return 0;
        });

        const isHardwareService = item?.TypeService?.toLowerCase().includes("hardware");
        const hasProjectManager = sortedPersons.some(([name]) => personManagerStatus[name]);
        let hardwareCostAssigned = false;
        const usedQuotationIds = new Set();

        // Calculate grandTotal for each person (same logic as SalesData)
        sortedPersons.forEach(([name, personData]) => {
          const totalHours = personData.totalHours;
          const personId = personData.personId;
          const roles = personData.roles;

          // Find employee
          const currentEmployee = employees.find(
            (emp) => emp.EmpEmail && emp.EmpEmail.toLowerCase() === personData.email?.toLowerCase()
          ) || employees.find((emp) => {
            const fullName = `${emp.EmpFirstName} ${emp.EmpLastName}`.trim().toLowerCase();
            return fullName === name.trim().toLowerCase();
          });

          const finalPersonId = currentEmployee?._id || personId;

          // Unique key for a quotation record only (never include current employee) so the same record is not reused
          const getQuotationRecordId = (q) => q._id || `${q.personid ?? "node"}_${q.designation ?? ""}`;

          let matchedQuotation = null;

          // Match by employee ID first from Settings → Hourly Rates & Expenses
          if (
            !isFinanceOrAccountingUser &&
            quotations &&
            quotations.length > 0 &&
            finalPersonId
          ) {
            const serviceTypeMatch = item?.TypeService?.toLowerCase() || "";
            matchedQuotation = quotations.find((q) => {
              const qDesignation = q.designation?.toLowerCase() || "";
              return (
                q.personid === finalPersonId &&
                qDesignation.includes(serviceTypeMatch) &&
                !usedQuotationIds.has(getQuotationRecordId(q))
              );
            });
          }

          if (!matchedQuotation) {
            matchedQuotation = item?.quotationDetails?.find(
              (q) =>
                String(q.personid) === String(finalPersonId) &&
                !usedQuotationIds.has(getQuotationRecordId(q))
            );
          }

          // Role-based fallback: still use quotation record id for used set so no reuse
          if (!matchedQuotation) {
            const isManager = roles.some((role) =>
              role.toLowerCase().includes("manager") || role.toLowerCase().includes("project manager")
            );
            const isOperations = roles.some((role) =>
              role.toLowerCase().includes("operations") || role.toLowerCase().includes("employee")
            );

            if (isManager) {
              const managerQuotes = item?.quotationDetails?.filter((q) => {
                const qDesignation = q.designation?.toLowerCase() || "";
                return (
                  !usedQuotationIds.has(getQuotationRecordId(q)) &&
                  (qDesignation.includes("project manager") ||
                   (qDesignation.includes("manager") && !qDesignation.includes("operations"))) &&
                  !qDesignation.includes("employee")
                );
              });
              matchedQuotation = managerQuotes?.find((q) =>
                q.designation?.toLowerCase().includes("project manager")
              ) || managerQuotes?.find((q) => !usedQuotationIds.has(getQuotationRecordId(q)));
            } else if (isOperations) {
              let employeeQuotes = [];
              if (
                !isFinanceOrAccountingUser &&
                quotations &&
                quotations.length > 0
              ) {
                const serviceTypeMatch = item?.TypeService?.toLowerCase() || "";
                employeeQuotes = quotations.filter((q) => {
                  const qDesignation = q.designation?.toLowerCase() || "";
                  return (
                    !usedQuotationIds.has(getQuotationRecordId(q)) &&
                    qDesignation.includes(serviceTypeMatch) &&
                    !qDesignation.includes("manager") &&
                    !qDesignation.includes("project manager") &&
                    (qDesignation.includes("operations") || qDesignation.includes("employee"))
                  );
                });
              }
              if (employeeQuotes.length === 0) {
                employeeQuotes = item?.quotationDetails?.filter((q) => {
                  const qDesignation = q.designation?.toLowerCase() || "";
                  return (
                    !usedQuotationIds.has(getQuotationRecordId(q)) &&
                    !qDesignation.includes("manager") &&
                    !qDesignation.includes("project manager") &&
                    (qDesignation.includes("operations") || qDesignation.includes("employee"))
                  );
                }) || [];
              }
              const quoteByPersonId = employeeQuotes.find((q) =>
                q.personid && q.personid === finalPersonId && !usedQuotationIds.has(getQuotationRecordId(q))
              );
              if (quoteByPersonId) {
                matchedQuotation = quoteByPersonId;
              } else if (employeeQuotes.length > 0) {
                matchedQuotation = employeeQuotes.find((q) => !usedQuotationIds.has(getQuotationRecordId(q)));
              }
            }
          }

          if (matchedQuotation) {
            usedQuotationIds.add(getQuotationRecordId(matchedQuotation));
          }

          const hourlyRate = (matchedQuotation?.hourlysalaray || 0) + (matchedQuotation?.overheadpercentage || 0);
          const totalCost = totalHours * hourlyRate;

          const descriptionTotal = getTotalDescriptionValue(
            item.ProjectID,
            finalPersonId,
            item.TypeService,
            isFinanceOrAccountingUser
              ? {
                  perdiem: item.projectPerdiem,
                  avgcost: item.projectAvgcost,
                }
              : undefined
          );

          const hardwareTotal = getTotalHardwareCostValue(
            item.ProjectID,
            finalPersonId,
            item.TypeService
          );

          const isOperationsRole = roles.some((role) =>
            role.toLowerCase().includes("operations")
          );
          const isProjectManagerRole = personManagerStatus[name];

          // Calculate grandTotal (same logic as SalesData)
          let grandTotal = 0;

          if (isHardwareService) {
            if (isOperationsRole) {
              if (!hardwareCostAssigned && !hasProjectManager) {
                grandTotal = hardwareTotal + descriptionTotal;
                hardwareCostAssigned = true;
              } else {
                grandTotal = descriptionTotal;
              }
            } else if (isProjectManagerRole) {
              grandTotal = hardwareTotal + descriptionTotal;
            } else {
              grandTotal = descriptionTotal + totalCost;
            }
          } else {
            grandTotal = descriptionTotal + totalCost;
          }

          totalExpenses += grandTotal;
        });
      });
    });

    return totalExpenses;
  };

  const showcolor = (row) => {
    const rowKey = getRowKey(row);
    const projectsGroupedByKey = financeDataProject.reduce((acc, project) => {
      const key = getRowKey(project);
      const existingGroup = acc.find((g) => g.key === key);
      if (existingGroup) {
        existingGroup.Projects.push(project);
      } else {
        acc.push({ key, ProjectName: project.ProjectName, Projects: [project] });
      }
      return acc;
    }, []);
    const matchingProjectGroup = projectsGroupedByKey?.find(
      (group) => group.key === rowKey
    );

    const allSalesPersonStatuses = matchingProjectGroup?.Projects?.flatMap(
      (project) =>
        project.Assignedperson.map((person) => person.salespersonstatus)
    );
    const uniqueStatuses = [...new Set(allSalesPersonStatuses)];

    return uniqueStatuses[0];
  };

  // Initialize quotedValues from TableData (which matches what's displayed in the table)
  useEffect(() => {
    if (TableData && TableData.length > 0) {
      const updatedValues = { ...quotedValues }; // Preserve existing edits
      const updatedComments = { ...comments };

      TableData.forEach((lead) => {
        const key = getRowKey(lead);
        if (updatedValues[key] === undefined) {
          updatedValues[key] = lead.QuotedValue || 0;
        }
        if (updatedComments[key] === undefined) {
          updatedComments[key] = lead.projectnotes || "";
        }
      });
      setQuotedValues(updatedValues);
      setComments(updatedComments);
    }
  }, [TableData, comments, quotedValues]);
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);
  const calculateGrossProfit = (row) => {
    const rowKey = getRowKey(row);
    const quotedValue = quotedValues[rowKey] !== undefined
      ? quotedValues[rowKey]
      : (row?.QuotedValue || 0);

    const matchedProjects = financeDataProject.filter(
      (proj) => getRowKey(proj) === rowKey
    );

    // CRM package: leads are not in financeDataProject; compute Gross Profit from Quoted Price and Quoted Expenses (lead.quotedExpense)
    if (!matchedProjects.length) {
      if (isCrmPackage) {
        const expenses = showExpensesop(row);
        const expensesNum = typeof expenses === "string"
          ? parseFloat(expenses.replace(/[^0-9.-]/g, "")) || 0
          : (typeof expenses === "number" ? expenses : parseFloat(expenses) || 0);
        const quotedNum = typeof quotedValue === "number"
          ? quotedValue
          : parseFloat(String(quotedValue || "").replace(/[^0-9.-]/g, "")) || 0;
        return quotedNum - expensesNum;
      }
      return "0";
    }

    // const projectIds = matchedProjects.map((proj) => proj._id);

    // const matchedWorkEntries = projectTrackingWorkEntries.filter((entry) =>
    //   projectIds.includes(entry.projectid)
    // );

    // const matchedDescriptionTasks = descriptionProjectTracking.filter((task) =>
    //   projectIds.includes(task.projectid)
    // );

    // const matchedHardware = hardwareProjectTracking.filter((task) =>
    //   projectIds.includes(task.projectid)
    // );

    // Calculate TaskList value (Software/Cloud/Installation)
    // let taskListValue = 0;
    // const groupedByPerson = {};
    // matchedWorkEntries.forEach((entry) => {
    //   if (!entry.assemblepartdescription && entry.hoursspent) {
    //     const personId = entry.taskownerid;
    //     if (!groupedByPerson[personId]) {
    //       groupedByPerson[personId] = {
    //         totalHours: 0,
    //         typeService: entry.typeservice,
    //       };
    //     }
    //     groupedByPerson[personId].totalHours += Number(entry.hoursspent) || 0;
    //   }
    // });

    // Object.entries(groupedByPerson).forEach(([personId, personData]) => {
    //   const employee = employees.find((emp) => emp._id === personId);
    //   const designation = employee?.servicetype || employee?.customRoles?.[0];

    //   if (designation) {
    //     const matchedQuote = quotations.find(
    //       (q) =>
    //         q.designation?.toLowerCase().includes(designation.toLowerCase()) &&
    //         q.personid === personId
    //     );

    //     if (matchedQuote) {
    //       const hourlyRate =
    //         (matchedQuote.hourlysalaray || 0) +
    //         (matchedQuote.overheadpercentage || 0);
    //       taskListValue += personData.totalHours * hourlyRate;
    //     }
    //   }
    // });

    // Calculate Description value
    // let descriptionValue = 0;
    // if (matchedDescriptionTasks.length > 0) {
    //   const installationTripByOwner = {};

    //   matchedDescriptionTasks.forEach((desc) => {
    //     if (desc.expenses) {
    //       descriptionValue += Number(desc.expenses) || 0;
    //     }

    //     if (
    //       desc.typeservice &&
    //       desc.typeservice.toLowerCase().includes("installation") &&
    //       desc.taskownerid &&
    //       desc.tripduration
    //     ) {
    //       if (!installationTripByOwner[desc.taskownerid]) {
    //         installationTripByOwner[desc.taskownerid] = desc.tripduration;
    //       } else {
    //         // Use the maximum trip duration for each owner
    //         installationTripByOwner[desc.taskownerid] = Math.max(
    //           installationTripByOwner[desc.taskownerid],
    //           desc.tripduration
    //         );
    //       }
    //     }
    //   });

    //   Object.values(installationTripByOwner).forEach((tripDuration) => {
    //     descriptionValue += tripDuration * perdiem + tripDuration * averagecost;
    //   });
    // }

    // Calculate Hardware value
    // let hardwareValue = 0;
    // if (matchedHardware.length > 0) {
    //   const hardwareByDescription = {};

    //   matchedHardware.forEach((hw) => {
    //     const description = hw.assemblepartdescription;

    //     if (!hardwareByDescription[description]) {
    //       hardwareByDescription[description] = hw;
    //     } else {
    //       if (
    //         Number(hw.totalprice) >
    //         Number(hardwareByDescription[description].totalprice)
    //       ) {
    //         hardwareByDescription[description] = hw;
    //       }
    //     }
    //   });

    //   Object.values(hardwareByDescription).forEach((hw) => {
    //     if (hw.totalprice) {
    //       hardwareValue += Number(hw.totalprice) || 0;
    //     }
    //   });
    // }

    const expenses = showExpensesop(row)
    // Convert expenses string to number if needed (handle formatted strings like "1,234.56")
    const expensesNum = typeof expenses === 'string' 
      ? parseFloat(expenses.replace(/[^0-9.-]/g, '')) || 0 
      : (typeof expenses === 'number' ? expenses : parseFloat(expenses) || 0);
    
    // const grandTotal = taskListValue + descriptionValue + hardwareValue;
    console.log("Calculated expenses for grossProfit ", rowKey, ":", quotedValue, expensesNum);

    const grossProfit = quotedValue - expensesNum;
    return grossProfit;
    // }
  };

  const handleCommentsChange = (row, value) => {
    const key = getRowKey(row);
    setComments((prev) => ({
      ...prev,
      [key]: value,
    }));

    // Clear previous timeout if it exists
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    // Set new timeout to call API after 1 second (debounce)
    debounceTimeoutRef.current = setTimeout(() => {
      updateComments(row, value);
    }, 1000);
  };

  const updateComments = async (selectedProject, newComment) => {
    try {
      const updatedLeadData = {
        projectnotes: newComment,
        ProjectName: selectedProject.ProjectName,
      };

      await dispatch(
        updateLead({ id: selectedProject.id, updatedData: updatedLeadData })
      ).unwrap();

      dispatch(fetchLeads());
      enqueueSnackbar("Notes Updated Successfully", {
        variant: "success",
      });
    } catch (error) {
      console.error("Error updating Notes:", error);
      enqueueSnackbar("Error updating Notes", {
        variant: "error",
      });
    }
  };

  return (
    <div className="rounded-md bg-white">
      <div className="relative  scroll-visible justify-center top-0">
        <div className="">
          <div>
            <div className="w-full max-h-[80vh] overflow-auto">
              <div className="w-full head sticky top-0 z-10 flex bg-[#e8e8e8]">
                <div className="flex-shrink-0 w-10 px-3 py-3 flex items-center justify-start text-center text-xs font-medium tracking-wider bg-[#e8e8e8] sticky left-0 z-20">
                  Sr. No
                </div>
                <div className="flex-shrink-0 w-32 px-3 py-3 flex gap-2 relative justify-between align-middle items-center text-center text-xs font-medium tracking-wider capitalize bg-[#e8e8e8] sticky left-[40px] z-20">
                  Group Name
                  <div className="flex flex-col gap-y-1 absolute right-1">
                    <div
                      className="cursor-pointer"
                      onClick={() => sortAlphabetically("groupname", "az")}
                    >
                      <ArrowDropUp color="white" sx={{ fontSize: "22px" }} />
                    </div>

                    <div
                      className="cursor-pointer -mt-2"
                      onClick={() => sortAlphabetically("groupname", "za")}
                    >
                      <ArrowDropDown color="white" sx={{ fontSize: "22px" }} />
                    </div>
                  </div>
                </div>
                <div className="flex-shrink-0 w-32 px-3 py-3 flex gap-2 relative justify-between align-middle items-center text-center text-xs font-medium tracking-wider capitalize bg-[#e8e8e8] sticky left-[168px] z-20">
                  Client Name
                  <div className="flex flex-col gap-y-1 absolute right-1">
                    <div
                      className="cursor-pointer"
                      onClick={() => sortAlphabetically("clientname", "az")}
                    >
                      <ArrowDropUp color="white" sx={{ fontSize: "22px" }} />
                    </div>

                    <div
                      className="cursor-pointer -mt-2"
                      onClick={() => sortAlphabetically("clientname", "za")}
                    >
                      <ArrowDropDown color="white" sx={{ fontSize: "22px" }} />
                    </div>
                  </div>
                </div>
                <div className="flex-shrink-0 w-32 px-3 py-3 flex gap-2 relative justify-between align-middle items-center text-center text-xs font-medium tracking-wider capitalize bg-[#e8e8e8] sticky left-[296px] z-20">
                  Project Name
                  <div className="flex flex-col gap-y-1 absolute right-1">
                    <div
                      className="cursor-pointer"
                      onClick={() => sortAlphabetically("projectname", "az")}
                    >
                      <ArrowDropUp color="white" sx={{ fontSize: "22px" }} />
                    </div>

                    <div
                      className="cursor-pointer -mt-2"
                      onClick={() => sortAlphabetically("projectname", "za")}
                    >
                      <ArrowDropDown color="white" sx={{ fontSize: "22px" }} />
                    </div>
                  </div>
                </div>
                <div className="flex-shrink-0 w-44 px-3 py-3 gap-2 flex justify-center align-middle items-center text-center text-xs font-medium  tracking-wider capitalize bg-[#e8e8e8]">
                  Quoted Price ({currency})
                  <div className="flex flex-col gap-y-1">
                    <div
                      className="cursor-pointer"
                      onClick={() => numericSort("za")}
                    >
                      <ArrowDropUp color="white" sx={{ fontSize: "22px" }} />
                    </div>

                    <div
                      className="cursor-pointer -mt-2"
                      onClick={() => numericSort("az")}
                    >
                      <ArrowDropDown color="white" sx={{ fontSize: "22px" }} />
                    </div>
                  </div>
                </div>
                <div className="flex-shrink-0 w-40 px-3 py-3 flex justify-center align-middle items-center text-center text-xs font-medium  tracking-wider capitalize bg-[#e8e8e8]">
                  {selectedServiceTypes === "ERP"
                    ? `Actual Expenses (${currency})`
                    : `Quoted Expenses (${currency})`}
                </div>
                <div className="flex-shrink-0 w-32 px-3 py-3 flex justify-center align-middle items-center text-center text-xs font-medium  tracking-wider capitalize bg-[#e8e8e8]">
                  Gross Profit ({currency})
                </div>
                <div className="flex-shrink-0 w-40 px-3 py-3 flex justify-center align-middle items-center text-center text-xs font-medium  tracking-wider capitalize bg-[#e8e8e8]">
                  G&A Expenses ({currency})
                </div>
                {/* <div className="flex-shrink-0 w-24 px-3 py-3 flex justify-center align-middle items-center text-center border text-xs font-medium  tracking-wider capitalize bg-black">
                          EBIT ({currency})
                        </div> */}
                <div className="flex-shrink-0 w-24 px-3 py-3 flex justify-center align-middle items-center text-center text-xs font-medium  tracking-wider capitalize bg-[#e8e8e8]">
                  Tax ({currency})
                </div>
                <div className="flex-shrink-0 w-44 flex flex-col justify-center align-middle items-center text-center text-xs font-medium  tracking-wider capitalize bg-[#e8e8e8]">
                  <div className="h-[50%] w-full flex justify-center align-middle items-center">
                    {" "}
                    Net Revenue
                  </div>
                  <div className="h-[50%] flex w-full">
                    <div className="w-[50%] flex justify-center align-middle items-center">
                      ({currency})
                    </div>
                    <div className="w-[50%] flex justify-center align-middle items-center">
                      (%)
                    </div>
                  </div>
                </div>
                <div className="flex-shrink-0 w-28 px-3 py-3 flex justify-center align-middle items-center text-center text-xs font-medium  tracking-wider capitalize bg-[#e8e8e8]">
                  Notes
                </div>
                <div className="flex-shrink-0  w-24 px-3 py-3 flex justify-center align-middle items-center text-center text-xs font-medium  capitalize bg-[#e8e8e8]">
                  Attachment
                </div>
              </div>

              <div className="w-full">
                {currentItems.map((row, index) => (
                  <>
                    <div
                      key={index}
                      className={` CRMandERPtableone  w-full flex`}
                    >
                      <div
                        className={`flex-shrink-0 w-10 px-3 py-3 flex items-center justify-start text-start border-b text-xs font-medium tracking-wider sticky left-0 z-[5] ${
                          showcolor(row) === "project ongoing"
                            ? "bg-orange-200"
                            : showcolor(row) === "project complete"
                            ? "bg-white"
                            : "bg-white"
                        }`}
                      >
                        {startIdx + index + 1}
                      </div>
                      <div
                        className={`flex-shrink-0 w-32 px-3 py-3 flex gap-2 justify-start align-middle items-center text-start border-b text-xs font-medium tracking-wider capitalize sticky left-[40px] z-[5] ${
                          showcolor(row) === "project ongoing"
                            ? "bg-orange-200"
                            : showcolor(row) === "project complete"
                            ? "bg-white"
                            : "bg-white"
                        }`}
                      >
                        {getGroupDisplayName(row)}
                      </div>
                      <div
                        className={`flex-shrink-0 w-32 px-3 py-3 flex gap-2 justify-start align-middle items-center text-start border-b text-xs font-medium tracking-wider capitalize sticky left-[168px] z-[5] ${
                          showcolor(row) === "project ongoing"
                            ? "bg-orange-200"
                            : showcolor(row) === "project complete"
                            ? "bg-white"
                            : "bg-white"
                        }`}
                      >
                        {row.ClientName}
                      </div>
                      <div
                        className={`flex-shrink-0 w-32 px-3 py-3 flex gap-2 justify-start align-middle items-center text-start border-b text-xs font-medium tracking-wider capitalize sticky left-[296px] z-[5] ${
                          showcolor(row) === "project ongoing"
                            ? "bg-orange-200"
                            : showcolor(row) === "project complete"
                            ? "bg-white"
                            : "bg-white"
                        }`}
                      >
                        {row.ProjectName}
                      </div>
                      <div
                        className={`flex-shrink-0 w-44 p-2 gap-2 flex justify-center align-middle items-center text-center border-b text-xs font-medium  tracking-wider capitalize ${
                          showcolor(row) === "project ongoing"
                            ? "bg-orange-200"
                            : showcolor(row) === "project complete"
                            ? "bg-white"
                            : ""
                        }`}
                      >
                        {USAComma(row.QuotedValue)}
                      </div>
                      <div
                        onClick={() => {
                          if (isCrmPackage) return;
                          if (showExpensesop(row,row.ProjectName) !== "0") {
                            setSelectedProjectName(row);
                            setCalculationTable(true);
                            setOpenDropdownIndex(openDropdownIndex === null);
                          }
                          handleTabClick("AllServiceTable");
                        }}
                        className={`quotedExpense ${
                          !isCrmPackage &&
                          showExpensesop(row,row.ProjectName) !== "0"
                            ? "cursor-pointer"
                            : ""
                        } relative group flex-shrink-0 w-40 px-3 py-3 flex justify-center align-middle items-center text-center border-b text-xs font-medium tracking-wider capitalize ${
                          showcolor(row) === "project ongoing"
                            ? "bg-orange-200"
                            : showcolor(row) === "project complete"
                            ? "bg-white"
                            : ""
                        }`}
                      >
                        {(formatTotalExpenseComma || USAComma)(
                          showExpensesop(row, row.ProjectName)
                        )}
                      </div>
                      <div
                        className={`GrossProfit flex-shrink-0 w-32  px-3 py-3 flex justify-center align-middle items-center text-center border-b text-xs font-medium tracking-wider capitalize text-wrap break-all 
        ${
                          calculateGrossProfit(row) < 0
            ? "text-red-500"
            : "text-black"
        } ${
                          showcolor(row) === "project ongoing"
                            ? "bg-orange-200"
                            : showcolor(row) === "project complete"
                            ? "bg-white"
                            : ""
                        }`}
                      >
                        {USAComma(calculateGrossProfit(row))}
                      </div>
                      <div
                        className={`G&AExpenses flex-shrink-0 w-40 px-3 py-3 flex justify-center align-middle items-center text-center border-b text-xs font-medium tracking-wider capitalize text-wrap break-all 
        ${
                          calculateGrossProfit(row) * (GandATax / 100) < 0
            ? "text-red-500"
            : "text-black"
        } ${
                          showcolor(row) === "project ongoing"
                            ? "bg-orange-200"
                            : showcolor(row) === "project complete"
                            ? "bg-white"
                            : ""
                        }`}
                      >
                        {USAComma(
                          calculateGrossProfit(row) *
                            (GandATax / 100)
                        )}
                      </div>

                      {/* <div
                                  className={`EBIT flex-shrink-0 w-24 px-3 py-3 flex justify-center align-middle items-center text-center border text-xs font-medium tracking-wider capitalize text-wrap break-all
      
        ${calculateGrossProfit(row) - (calculateGrossProfit(row) * (GandATax / 100)) < 0 ? "text-red-500" : "text-black"}`}
                                >
                                  ${USAComma(
                                    calculateGrossProfit(row) -
                                    calculateGrossProfit(row) * (GandATax / 100)
                                  )}
                                </div> */}

                      <div
                        className={`Tax flex-shrink-0 w-24  px-3 py-3 flex justify-center align-middle items-center text-center border-b text-xs font-medium tracking-wider capitalize text-wrap break-all
        
        ${
                          calculateGrossProfit(row) * (taxValue / 100) < 0
            ? "text-red-500"
            : "text-black"
        } ${
                          showcolor(row) === "project ongoing"
                            ? "bg-orange-200"
                            : showcolor(row) === "project complete"
                            ? "bg-white"
                            : ""
                        }`}
                      >
                        {USAComma(
                          calculateGrossProfit(row) *
                            (taxValue / 100)
                        )}
                      </div>

                      <div
                        className={`flex-shrink-0 w-44 flex  justify-center align-middle items-center text-center border-b text-xs font-medium  tracking-wider capitalize text-wrap break-all ${
                          showcolor(row) === "project ongoing"
                            ? "bg-orange-200"
                            : showcolor(row) === "project complete"
                            ? "bg-white"
                            : ""
                        }`}
                      >
                        <div
                          className={`NetRevenue w-[50%]  flex justify-center align-middle items-center text-wrap break-all
        ${
                            calculateGrossProfit(row) -
                            calculateGrossProfit(row) * (GandATax / 100) -
                            calculateGrossProfit(row) * (taxValue / 100) <
          0
            ? "text-red-500"
            : "text-black"
        }`}
                        >
                          {USAComma(
                            calculateGrossProfit(row) -
                            calculateGrossProfit(row) *
                                (GandATax / 100) -
                            calculateGrossProfit(row) *
                                (taxValue / 100)
                          )}
                        </div>

                        <div
                          className={`NetRevenuePercentage w-[50%] flex justify-center align-middle items-center text-wrap break-all
        ${
                            ((calculateGrossProfit(row) -
                            calculateGrossProfit(row) * (GandATax / 100) -
                            calculateGrossProfit(row) * (taxValue / 100)) /
            quotedValues[getRowKey(row)]) *
            100 <
          0
            ? "text-red-500"
                            : ((calculateGrossProfit(row) -
                              calculateGrossProfit(row) * (GandATax / 100) -
                              calculateGrossProfit(row) * (taxValue / 100)) /
                quotedValues[getRowKey(row)]) *
                100 <
              10
            ? "text-orange-600"
                              : "text-black"
                          } ${
                            showcolor(row) === "project ongoing"
                              ? "bg-orange-200"
                              : showcolor(row) ===
                                "project complete"
                              ? "bg-white"
                              : ""
                          }`}
                        >
                          {(() => {
                            const grossProfit = calculateGrossProfit(row);
                            const quotedValue = quotedValues[getRowKey(row)];

                            const value =
                              ((grossProfit -
                                grossProfit * (GandATax / 100) -
                                grossProfit * (taxValue / 100)) /
                                quotedValue) *
                              100;

                            return isFinite(value) && !isNaN(value)
                              ? value.toFixed(2)
                              : "0";
                          })()}
                        </div>
                      </div>
                      <div
                        className={`flex-shrink-0 w-28 break-words text-wrap flex justify-center items-center text-center border-b text-xs font-medium tracking-wider capitalize ${
                          showcolor(row) === "project ongoing"
                            ? "bg-orange-200"
                            : showcolor(row) === "project complete"
                            ? "bg-white"
                            : ""
                        }`}
                      >
                        <textarea
                          className={`leadComments w-28 h-full outline-none text-center break-words whitespace-normal resize-none overflow-x-hidden ${
                            showcolor(row) === "project ongoing"
                              ? "bg-orange-200"
                              : showcolor(row) ===
                                "project complete"
                              ? "bg-white"
                              : ""
                          }`}
                          value={comments[getRowKey(row)] || ""}
                          onChange={(e) =>
                            handleCommentsChange(row, e.target.value)
                          }
                          placeholder="Enter Notes"
                        />
                      </div>

                      <div
                        className={`flex-shrink-0 cursor-pointer w-24 px-3 py-3 flex justify-center align-middle items-center text-center border-b text-xs font-medium  tracking-wider capitalize ${
                          showcolor(row) === "project ongoing"
                            ? "bg-orange-200"
                            : showcolor(row) === "project complete"
                            ? "bg-white"
                            : ""
                        }`}
                      >
                        <AttachFileIcon
                          onClick={() => {
                            setPopupProject(row);
                            setOpenDropdownIndex(openDropdownIndex === null);
                          }} // open popup for this project
                          className="cursor-pointer rotate-12"
                        />
                        {/* <FinanceFileUpload
                                                    row={row}
                                                    setOpenDropdownIndex={setOpenDropdownIndex}
                                                    openDropdownIndex={openDropdownIndex}
                                                /> */}
                      </div>
                    </div>
                  </>
                ))}
              </div>
            </div>
          </div>

          <div className="pagination border flex justify-between items-center bg-white pt-2 pb-2 p-1 rounded-b-md">
            <div>
              {totalItems > 0 ? (
                <>
                  Showing {startIdx + 1} to {Math.min(endIdx, totalItems)} of{" "}
                  {totalItems} Entries
                </>
              ) : (
                <>Showing 0 Entries</>
              )}
            </div>
            {totalPages > 1 && (
              <div>
                <button
                  onClick={() => setCurrentPage(1)}
                  className="px-2 py-2 mx-1 border rounded"
                  disabled={currentPage === 1}
                  title="First Page"
                >
                  <KeyboardDoubleArrowLeftIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  className="px-2 py-2 mx-2 border rounded"
                  disabled={currentPage === 1}
                >
                  <ChevronRightIcon className="w-5 h-5 rotate-180" />

                  {/* Previous */}
                </button>

                {Array.from({ length: 3 }, (_, index) => {
                  // Calculate the page number for the current range
                  const pageNumber = currentPage - 1 + index;

                  // Ensure the pageNumber stays within bounds
                  if (pageNumber >= 1 && pageNumber <= totalPages) {
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => setCurrentPage(pageNumber)}
                        className={`px-4 py-2 mx-2 border rounded ${
                          currentPage === pageNumber ? "bg-gray-300" : ""
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  }

                  // Return null for out-of-bounds values
                  return null;
                })}

                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  className="px-2 py-2 mx-2 border rounded"
                  disabled={currentPage === totalPages}
                >
                  <ChevronRightIcon className="w-5 h-5" />

                  {/* Next */}
                </button>

                <button
                  onClick={() => setCurrentPage(totalPages)}
                  className="px-2 py-2 mx-1 border rounded"
                  disabled={currentPage === totalPages}
                  title="Last Page"
                >
                  <KeyboardDoubleArrowRightIcon className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>

          {calculationTable && selectedTab === "AllServiceTable" && !isCrmPackage && (
            <>
              <div ref={AllServiceTableRef}>
                <FinanceData
                  filteredQuotation={financeFilteredQuotation}
                  selectedProject={selectedProjectName}
                  projects={projects}
                  calculatedProjects={calculatedProjects}
                  tasks={financeDetailTasks}
                  items={hardwareSource}
                  formatOtherExpense={formatOtherExpenseComma}
                  formatTotalExpense={formatTotalExpenseComma}
                  useProjectAssignedRates={isFinanceOrAccountingUser}
                />
              </div>
            </>
          )}

          {popupProject && (
            <FinanceFileUpload
              setPopupProject={setPopupProject}
              row={popupProject}
              setOpenDropdownIndex={setOpenDropdownIndex}
              openDropdownIndex={openDropdownIndex}
            />
          )}
        </div>
      </div>

     
    </div>
  );
};

export default ActualFinanceView;
